import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout"
import type { EventContentArg, EventMountArg } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import FullCalendar from "@fullcalendar/react"
import { Head, usePage } from "@inertiajs/react"
import { IconList, IconPlus } from "@tabler/icons-react"
import { useCallback, useRef, useState } from "react"

interface BalanceGroup {
  balance_type: string
  total_remaining: number
  total_available: number
  years: { year: number; remaining_days: number; days_available: number }[]
}

interface ProjectOption {
  id: number
  name: string
  customer_name: string
}

interface CustomerOption {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  balances: BalanceGroup[]
  events_url: string
  new_request_url: string
  requests_url: string
  user_id: number
  projects: ProjectOption[]
  customers: CustomerOption[]
}

const STATUS_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  approved: { bg: "#dcfce7", border: "#16a34a", text: "#14532d" },
  pending: { bg: "#fef9c3", border: "#ca8a04", text: "#713f12" },
  denied: { bg: "#fee2e2", border: "#dc2626", text: "#7f1d1d" },
  holiday: { bg: "#fef3c7", border: "#d97706", text: "#78350f" },
}

function createTextNode(text: string): HTMLElement {
  const span = document.createElement("span")
  span.textContent = text
  return span
}

export default function CalendarIndex() {
  const { balances, events_url, new_request_url, requests_url, user_id, projects, customers } = usePage<Props>().props

  const calendarRef = useRef<FullCalendar>(null)
  const [projectFilter, setProjectFilter] = useState("")
  const [customerFilter, setCustomerFilter] = useState("")

  // Keep filter values in a ref so the extraParams callback always reads the latest
  const filtersRef = useRef({ projectFilter: "", customerFilter: "" })
  filtersRef.current = { projectFilter, customerFilter }

  const extraParams = useCallback(() => {
    const params: Record<string, string> = {}
    if (filtersRef.current.projectFilter) params.project_id = filtersRef.current.projectFilter
    if (filtersRef.current.customerFilter) params.customer_id = filtersRef.current.customerFilter
    return params
  }, [])

  function handleProjectChange(value: string) {
    setProjectFilter(value === "all" ? "" : value)
    setTimeout(() => calendarRef.current?.getApi().refetchEvents(), 0)
  }

  function handleCustomerChange(value: string) {
    setCustomerFilter(value === "all" ? "" : value)
    setTimeout(() => calendarRef.current?.getApi().refetchEvents(), 0)
  }

  const handleDateSelect = useCallback(
    (info: { startStr: string; endStr: string }) => {
      const endDate = new Date(info.endStr)
      endDate.setDate(endDate.getDate() - 1)
      const endStr = endDate.toISOString().split("T")[0]
      window.location.href = `${new_request_url}?start_date=${info.startStr}&end_date=${endStr}`
    },
    [new_request_url],
  )

  const handleEventClick = useCallback((info: { event: { extendedProps: Record<string, unknown> } }) => {
    const props = info.event.extendedProps
    if (props.type === "holiday") return
    if (props.timeOffRequestId) {
      window.location.href = `/time_off_requests/${props.timeOffRequestId}`
    }
  }, [])

  return (
    <>
      <Head title="Calendar - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold tracking-tight">Calendar</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={requests_url}>
                <IconList className="mr-1.5 h-4 w-4" />
                Requests
              </a>
            </Button>
            <Button size="sm" asChild>
              <a href={new_request_url}>
                <IconPlus className="mr-1.5 h-4 w-4" />
                New Request
              </a>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Project:</span>
            <Select value={projectFilter || "all"} onValueChange={handleProjectChange}>
              <SelectTrigger className="h-8 w-[180px] text-sm">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Client:</span>
            <Select value={customerFilter || "all"} onValueChange={handleCustomerChange}>
              <SelectTrigger className="h-8 w-[180px] text-sm">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Balance Cards */}
        {balances.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {balances.map((balance) => {
              const usedPct =
                balance.total_available > 0
                  ? Math.round(((balance.total_available - balance.total_remaining) / balance.total_available) * 100)
                  : 0

              return (
                <div key={balance.balance_type} className="rounded-lg border bg-card p-3">
                  <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                    {balance.balance_type}
                  </p>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-xl font-semibold tabular-nums">{balance.total_remaining}</span>
                    <span className="text-muted-foreground text-xs">/ {balance.total_available}</span>
                  </div>
                  <div className="bg-muted mt-2 h-1 w-full overflow-hidden rounded-full">
                    <div
                      className="bg-primary h-full rounded-full transition-all"
                      style={{ width: `${Math.min(100, 100 - usedPct)}%` }}
                    />
                  </div>
                  {balance.years.length > 1 && (
                    <div className="mt-1.5 space-y-0.5">
                      {balance.years.map((y) => (
                        <div
                          key={y.year}
                          className="text-muted-foreground flex justify-between text-[11px] tabular-nums"
                        >
                          <span>{y.year}</span>
                          <span>
                            {y.remaining_days}/{y.days_available}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Calendar */}
        <div className="fc-clean min-h-0 flex-1">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            selectable
            selectMirror
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,dayGridWeek",
            }}
            events={{
              url: events_url,
              format: "json",
              extraParams,
            }}
            eventDataTransform={(eventData) => {
              // Convert background holidays to regular block events so they're readable
              if (eventData.display === "background") {
                return { ...eventData, display: "block" }
              }
              return eventData
            }}
            eventContent={(arg: EventContentArg) => {
              const ext = arg.event.extendedProps
              if (ext.type === "holiday") {
                return { domNodes: [createTextNode(arg.event.title)] }
              }
              const container = document.createElement("div")
              container.style.cssText = "overflow:hidden;white-space:nowrap;text-overflow:ellipsis;line-height:1.3"

              const typeLine = document.createElement("div")
              typeLine.style.cssText = "font-weight:600;font-size:0.75rem"
              typeLine.textContent = ((ext.requestType as string) || "")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c: string) => c.toUpperCase())
              container.appendChild(typeLine)

              const detailLine = document.createElement("div")
              detailLine.style.cssText = "font-size:0.6875rem;opacity:0.8"
              const parts: string[] = []
              if (ext.userName) parts.push(ext.userName as string)
              const projectNames = ext.projectNames as string[] | undefined
              if (projectNames && projectNames.length > 0) {
                parts.push(projectNames.join(", "))
              }
              detailLine.textContent = parts.join(" · ")
              if (parts.length > 0) container.appendChild(detailLine)

              return { domNodes: [container] }
            }}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventDidMount={(info: EventMountArg) => {
              const isHoliday = info.event.extendedProps.type === "holiday"
              const status = isHoliday ? "holiday" : (info.event.extendedProps.status as string)
              const s = STATUS_STYLES[status]
              if (!s) return

              const el = info.el
              el.style.setProperty("background-color", s.bg, "important")
              el.style.setProperty("border", "none", "important")
              el.style.setProperty("border-left", `3px solid ${s.border}`, "important")
              el.style.borderRadius = "4px"

              // The text lives inside .fc-event-main, which FC styles with color: var(--fc-event-text-color)
              const main = el.querySelector(".fc-event-main") as HTMLElement | null
              if (main) {
                main.style.setProperty("color", s.text, "important")
                main.style.fontSize = "0.75rem"
                main.style.fontWeight = "500"
                main.style.padding = "2px 4px"
                main.style.lineHeight = "1.4"
              }

              if (!isHoliday) {
                const isOwn = info.event.extendedProps.userId === user_id
                if (!isOwn) el.style.opacity = "0.6"
              }
            }}
            height="100%"
            dayMaxEvents={3}
            fixedWeekCount={false}
          />
        </div>
      </div>

      <style>{`
        /* Clean, minimal FullCalendar overrides */
        .fc-clean .fc {
          height: 100%;
          font-family: inherit;
        }

        .fc-clean .fc .fc-toolbar {
          margin-bottom: 1rem;
          gap: 0.5rem;
        }

        .fc-clean .fc .fc-toolbar-title {
          font-size: 1rem;
          font-weight: 600;
        }

        .fc-clean .fc .fc-button {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--foreground);
          font-size: 0.8125rem;
          padding: 0.25rem 0.75rem;
          border-radius: 0.375rem;
          font-weight: 500;
          text-transform: capitalize;
          box-shadow: none;
        }

        .fc-clean .fc .fc-button:hover {
          background: var(--accent);
        }

        .fc-clean .fc .fc-button-active,
        .fc-clean .fc .fc-button:active {
          background: var(--primary) !important;
          color: var(--primary-foreground) !important;
          border-color: var(--primary) !important;
        }

        .fc-clean .fc .fc-button-group > .fc-button {
          border-radius: 0;
        }

        .fc-clean .fc .fc-button-group > .fc-button:first-child {
          border-radius: 0.375rem 0 0 0.375rem;
        }

        .fc-clean .fc .fc-button-group > .fc-button:last-child {
          border-radius: 0 0.375rem 0.375rem 0;
        }

        .fc-clean .fc .fc-prev-button,
        .fc-clean .fc .fc-next-button {
          padding: 0.25rem 0.5rem;
        }

        /* Table / Grid */
        .fc-clean .fc th {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--muted-foreground);
          text-transform: uppercase;
          letter-spacing: 0.025em;
          padding: 0.5rem 0;
          border-color: var(--border);
        }

        .fc-clean .fc td {
          border-color: var(--border);
        }

        .fc-clean .fc .fc-scrollgrid {
          border-color: var(--border);
          border-radius: 0.5rem;
          overflow: hidden;
          border-width: 1px;
        }

        .fc-clean .fc .fc-col-header {
          background: var(--muted);
        }

        /* Day cells */
        .fc-clean .fc .fc-daygrid-day {
          min-height: 5.5rem;
        }

        .fc-clean .fc .fc-daygrid-day-number {
          font-size: 0.8125rem;
          padding: 0.375rem 0.5rem;
          color: var(--foreground);
        }

        .fc-clean .fc .fc-day-today {
          background: oklch(from var(--primary) l c h / 0.06) !important;
        }

        .fc-clean .fc .fc-day-today .fc-daygrid-day-number {
          font-weight: 700;
          color: var(--primary);
        }

        .fc-clean .fc .fc-day-other .fc-daygrid-day-number {
          color: var(--muted-foreground);
          opacity: 0.5;
        }

        /* Events */
        .fc-clean .fc .fc-daygrid-event {
          margin: 1px 4px;
          border-radius: 4px;
          font-size: 0.75rem;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fc-clean .fc .fc-event {
          cursor: pointer;
        }

        .fc-clean .fc .fc-h-event {
          border: none !important;
          background: none !important;
        }

        .fc-clean .fc .fc-h-event .fc-event-main {
          color: inherit !important;
        }

        .fc-clean .fc .fc-daygrid-dot-event {
          padding: 1px 4px;
        }

        /* More events link */
        .fc-clean .fc .fc-daygrid-more-link {
          font-size: 0.6875rem;
          color: var(--muted-foreground);
          font-weight: 500;
          padding: 0 4px;
        }

        /* Selection highlight */
        .fc-clean .fc .fc-highlight {
          background: oklch(from var(--primary) l c h / 0.1);
        }
      `}</style>
    </>
  )
}

CalendarIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
